<template>
  <v-dialog
    v-model="displayed"
    max-width="600"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card>
      <v-card-title class="justify-center text-uppercase headline"
        >Create task
      </v-card-title>
      <v-divider />
      <v-form ref="create-task-form">
        <div class="px-10 pb-5">
          <ant-input label="Title">
            <template #input-field>
              <div class="d-flex">
                <v-text-field
                  v-model="task.title"
                  :rules="[rules.required]"
                  dense
                  filled
                  hide-details
                  placeholder="title"
                />
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      class="ml-2"
                      icon
                      @click="isPrivate = !isPrivate"
                      v-on="on"
                    >
                      <v-icon :color="isPrivate ? 'primary' : ''"
                        >{{ isPrivate ? 'mdi-lock' : 'mdi-lock-open-outline' }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{
                    isPrivate ? 'Make task public' : 'Make task private'
                  }}</span>
                </v-tooltip>
              </div>
            </template>
          </ant-input>
          <div class="d-flex">
            <ant-input class="mr-2" label="Priority" style="flex: 1">
              <template #input-field>
                <v-select
                  v-model="task.priority"
                  :items="['low', 'normal', 'high', 'urgent']"
                  :rules="[rules.required]"
                  dense
                  filled
                  hide-details
                  placeholder="normal"
                >
                  <template #selection="{ item, index }">
                    <v-icon :color="getPriorityColor(item)" class="mr-2"
                      >{{ getPriorityIcon(item) }}
                    </v-icon>
                    {{ item }}
                  </template>
                  <template #item="{ item, attrs, on }">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-list-item-icon>
                        <v-icon :color="getPriorityColor(item)"
                          >{{ getPriorityIcon(item) }}
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>{{ item }}</v-list-item-title>
                    </v-list-item>
                  </template>
                </v-select>
              </template>
            </ant-input>
            <ant-input label="Project" style="flex: 2">
              <template #input-field>
                <v-combobox
                  v-model="task.project"
                  :disabled="project !== null"
                  :items="projects"
                  :rules="[rules.required]"
                  clearable
                  dense
                  filled
                  hide-details
                  item-text="name"
                  placeholder="project"
                  return-object
                />
              </template>
            </ant-input>
          </div>
          <div id="label-wrapper" class="mt-5">
            <v-tooltip
              :open-on-click="false"
              :open-on-hover="!task?.project"
              activator="#label-wrapper"
              bottom
            >
              <span>Choose project first</span>
            </v-tooltip>
            <task-label
              :disabled="!task?.project"
              :labels="task.labels"
              :project-id="task?.project?.id"
              editing
              @on-change="onLabelChange"
            />
          </div>
          <ant-input label="Executed by">
            <template #input-field>
              <v-combobox
                v-model="task.assigned_user"
                :disabled="!task.project"
                :items="users"
                clearable
                item-text="email"
                item-value="id"
                placeholder="email"
                single-line
              />
            </template>
          </ant-input>

          <div class="d-flex">
            <ant-input is-optional label="Due date">
              <template #input-field>
                <v-menu
                  v-model="menus.due_date"
                  :close-on-content-click="false"
                  min-width="auto"
                  offset-y
                  transition="scale-transition"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="task.due"
                      :placeholder="today"
                      clearable
                      dense
                      filled
                      hide-details
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="task.due"
                    no-title
                    scrollable
                    @input="menus.due_date = false"
                  />
                </v-menu>
              </template>
            </ant-input>

            <ant-input class="mx-2" is-optional label="Start date">
              <template #input-field>
                <v-menu
                  v-model="menus.start_date"
                  :close-on-content-click="false"
                  min-width="auto"
                  offset-y
                  transition="scale-transition"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="task.planned_start"
                      :placeholder="today"
                      clearable
                      dense
                      filled
                      hide-details
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="task.planned_start"
                    no-title
                    scrollable
                    @input="menus.start_date = false"
                  />
                </v-menu>
              </template>
            </ant-input>
            <ant-input is-optional label="End date">
              <template #input-field>
                <v-menu
                  v-model="menus.end_date"
                  :close-on-content-click="false"
                  min-width="auto"
                  offset-y
                  transition="scale-transition"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="task.planned_end"
                      :placeholder="today"
                      clearable
                      dense
                      filled
                      hide-details
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="task.planned_end"
                    no-title
                    scrollable
                    @input="menus.end_date = false"
                  />
                </v-menu>
              </template>
            </ant-input>
          </div>
          <ant-input v-if="task.project !== null" is-optional label="Sbscode">
            <template #input-field>
              <v-combobox
                v-model="task.sbscode"
                :items="sbsRecords"
                :loading="sbsLoading"
                clearable
                dense
                filled
                hide-details
                item-text="code"
                item-value="code"
                placeholder="Sbscode"
              >
                <template #item="{ item }">
                  <div class="d-flex">
                    {{ item.label ? item.label : '-' }}
                    <v-divider class="mx-2" vertical />
                    <div class="font-italic fs-12">{{ item.code }}</div>
                  </div>
                </template>
              </v-combobox>
            </template>
          </ant-input>
          <slot name="add-task-options" />
          <ant-input is-optional label="Description">
            <template #input-field>
              <v-textarea
                v-model="task.description"
                :rules="[rules.maxLength]"
                filled
                placeholder="Please add a task description here"
              />
            </template>
          </ant-input>
          <v-spacer />
        </div>
      </v-form>

      <v-card-actions class="ant-border-top ant-dialog-actions-bg">
        <v-spacer />
        <v-btn color="error" small text @click.stop="closeDialog">
          Cancel
        </v-btn>
        <v-btn color="primary" elevation="0" small @click.stop="saveTask">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import {
  getProjectMembersWithTaskPermissions,
  getProjects,
} from '@/services/api/projects.api';
import { mapGetters } from 'vuex';
import TaskHelper from '@/services/task-helper';
import { getSbsRecords } from '@/services/api/sbs.api';
import AntInput from '@/components/AntInput.vue';
import TaskLabel from '@/components/Tasks/TaskLabel.vue';

export default {
  name: 'AddTaskDialog',
  components: { TaskLabel, AntInput },
  props: {
    parent: {
      type: String,
      default: null,
    },
    returnCreate: {
      type: Boolean,
      default: false,
    },
    project: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data: () => {
    return {
      isPrivate: false,
      displayed: false,
      today: moment().format('YYYY-MM-DD'),
      task: {
        assigned_user: null,
        labels: [],
      },
      users: [],
      projects: [],
      menus: {
        start_date: false,
        end_date: false,
        due_date: false,
      },
      rules: {
        required: (value) => !!value || 'Required.',
        maxLength: (value) =>
          value ? value.length < 250 || 'Max 250 characters' : true,
      },
      requestStatus: '',
      sbsRecords: [],
      sbsLoading: false,
    };
  },
  computed: {
    ...mapGetters(['selectedLicense', 'authenticatedUser']),
  },
  watch: {
    async displayed(value) {
      if (value) {
        this.projects = await getProjects({
          'filter[license]': this.selectedLicense.id,
          'filter[tasks]': true,
        });
        if (this.project) {
          this.task.project = this.project;
        }

        this.task.assigned_user = this.authenticatedUser;
      } else {
        this.closeDialog();
      }
    },
    async 'task.project'(newValue, oldValue) {
      if (newValue && newValue?.id !== oldValue?.id) {
        this.sbsLoading = true;
        this.sbsRecords = await getSbsRecords(newValue.id, {});
        this.users = await getProjectMembersWithTaskPermissions(newValue.id);
        this.sbsLoading = false;
      }
    },
  },
  methods: {
    closeDialog() {
      if (this.requestStatus !== 'loading') {
        this.task = {
          labels: [],
        };
        this.$refs['create-task-form'].reset();
        this.displayed = false;
        this.requestStatus = '';
        this.$emit('closeDialog');
      }
    },
    async saveTask() {
      if (this.$refs['create-task-form'].validate()) {
        if (!this.task.title) {
          this.$store.commit('showNotification', {
            content: 'Please fill in the tasks title',
            color: 'error',
          });
          return;
        }
        if (!this.task.assigned_user) {
          this.$store.commit('showNotification', {
            content: 'Please add an assigned user',
            color: 'error',
          });
          return;
        }

        let body = {
          parent: this.parent,
          project: this.task.project ? this.task.project.id : null,
          title: this.task.title,
          priority: this.task.priority,
          description: this.task.description,
          assigned_to: this.task.assigned_user.id,
          planned_start: this.task.planned_start,
          planned_end: this.task.planned_end,
          due: this.task.due,
          labels: this.task.labels.map((l) => l.id),
          sbscode: this.task.project
            ? this.task.sbscode
              ? this.task.sbscode.code
              : null
            : null,
          license: this.selectedLicense.id,
          is_private: this.isPrivate,
        };

        if (this.returnCreate) {
          this.$emit('createTask', body);
        } else {
          this.requestStatus = 'loading';

          this.$store
            .dispatch('createTask', { body })
            .then((task) => {
              this.requestStatus = 'success';
              this.$store.commit('showNotification', {
                content: `Successfully created task '${task.title}'`,
                color: 'success',
              });
              this.$emit('success');
              this.closeDialog();
              if (this.parent) {
                this.$store.dispatch('reloadDetailTask', this.parent);
              }
            })
            .catch((error) => {
              this.requestStatus = 'error';
            });
        }
      }
    },
    getPriorityIcon(priority) {
      return TaskHelper.getPriorityIcon(priority);
    },
    getPriorityColor(priority) {
      return TaskHelper.getPriorityColor(priority);
    },
    onLabelChange(labels) {
      this.task.labels = labels;
    },
  },
};
</script>

<style scoped></style>
